"use client";
import { StyledInputField } from "@/components/styledInputField/styledInputField";
import { UseFormReturn } from "react-hook-form";
import { IGiftCardFormFields } from "../types";
import { CurrencyCode } from "@/dto/currencyCodes";
import {
  getCurrencyCodeFromCountry,
  getCurrencySymbol,
} from "@/utils/currencyUtils";
import { maxAmount, minAmount } from "@/utils/inputValidators";
import { amountInputTheme } from "@/utils/themes";
import {
  GIFT_AMOUNT_MIN,
  GIFT_AMOUNT_MAX,
  GIFT_AMOUNT_DEFAULT,
} from "@/utils/giftCardUtils";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useId } from "react";
import { Countries } from "country-to-currency";
import { Locale } from "@/dto/locale";

interface IAmountComponent {
  currency?: CurrencyCode;
  min?: number;
  max?: number;
  form: UseFormReturn<IGiftCardFormFields>;
  giftCardSellingEnabled: boolean;
  onBlurred?: any;
  infoMessage: string;
  setInfoMessage: any;
  merchantColor?: string;
}

export const AmountComponent = ({
  currency,
  min = GIFT_AMOUNT_MIN,
  max = GIFT_AMOUNT_MAX,
  form,
  giftCardSellingEnabled,
  onBlurred,
  infoMessage,
  setInfoMessage,
  merchantColor,
}: IAmountComponent) => {
  const t = useTranslations("AmountComponent");
  const userLocale = useLocale();
  const formatter = useFormatter();

  const { setValue, watch, trigger } = form;
  const amountLabelId = useId();

  const formattedMin = formatter.number(min, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    currencyDisplay: "symbol",
  });
  const formattedMax = formatter.number(max, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    currencyDisplay: "symbol",
  });

  const userCountry = userLocale.split("-")[1]?.toUpperCase() as Countries;
  const userCurrency = getCurrencyCodeFromCountry(userCountry || "US");
  const currencySymbol = getCurrencySymbol(userLocale as Locale, currency!);

  // Compare user's currency with provided currency. If they match, show symbol only. If not, show currency code to avoid confusion.
  const inputPrefix =
    currency === userCurrency ? currencySymbol : `${currency} `;

  const rawValue = watch("amount");
  const value = rawValue?.toString() ?? "";

  // Set default info message on mount if not provided
  const defaultInfoMessage = t("defaultInfoMessage", {
    min: formattedMin,
    max: formattedMax,
  });

  // If infoMessage is not set, use defaultInfoMessage
  const infoMsgToShow = infoMessage || defaultInfoMessage;

  const handleBlur = () => {
    const parsed = Number.parseFloat(value);
    let clamped;
    let customError = defaultInfoMessage;
    if (value === "" || Number.isNaN(parsed)) {
      clamped = GIFT_AMOUNT_DEFAULT;
    } else if (parsed < min) {
      clamped = min;
      customError = t("minAmountError", { min: formattedMin });
    } else if (parsed > max) {
      clamped = max;
      customError = t("maxAmountError", { max: formattedMax });
    } else {
      clamped = parsed;
    }
    setInfoMessage(customError);
    setValue("amount", clamped, { shouldValidate: true });
    trigger("amount");
    onBlurred?.(clamped);
  };

  return (
    <div data-testid={applyDataTestId(pom.giftCard.giftCardAmountInput)}>
      <div className="flex flex-col gap-5 w-full md:w-1/2">
        <p
          id={amountLabelId}
          className="text-lg not-italic font-semibold leading-[1.875rem]"
        >
          {t("selectAmount")}
        </p>
        <StyledInputField
          theme={amountInputTheme}
          type="numeric"
          prefix={inputPrefix}
          label={t("amountLabel")}
          className="[&_.MuiOutlinedInput-root]:bg-white"
          form={form}
          onBlur={handleBlur}
          name="amount"
          ariaLabelledby={amountLabelId}
          validator={{
            minAmount: minAmount(min),
            maxAmount: maxAmount(max),
          }}
          disabled={!giftCardSellingEnabled}
          onKeyDown={(e: any) => {
            const invalidKeys = [".", ","];
            if (invalidKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
          merchantColor={merchantColor}
        />
      </div>
      {infoMsgToShow && (
        <p className="text-sm text-[#727272] mt-1">{infoMsgToShow}</p>
      )}
    </div>
  );
};
